/**
 * Wizard step 3 — subdomains or folders, chosen by the customer once the
 * languages are picked (product decision 15.09.2026; replaces the silent
 * automatic choice of M2, 27.07).
 *
 * What the domain can actually do comes from WizardService.Detect, which has
 * been running in the background since step 1:
 *   supported   — both work; folders recommended (one domain, one record set)
 *   unsupported — folders shown but disabled, with the provider named
 *   unknown     — both shown; folders carry the condition they depend on
 */

import { useState } from 'react'

import type { IWebsiteMode } from '@glocal/schemas'

import { Chip } from '../../components/chip'

import type { Detect } from './types'

interface StyleCard {
	id: IWebsiteMode
	name: string
	example: (host: string) => string
	good: string[]
	cost: string[]
}

const STYLES: StyleCard[] = [
	{
		id: 'subdomain',
		name: 'Subdomains',
		example: (h) => `es.${h}`,
		good: ['Works with any DNS provider', 'Languages go live one by one'],
		cost: ['A few records per language', 'Each language builds its own SEO'],
	},
	{
		id: 'subfolder',
		name: 'Folders',
		example: (h) => `${h}/es/`,
		good: ['Best for SEO — one domain', 'One set of records for every language'],
		cost: ['DNS must point your root domain at us', 'Plus an address we fetch your site from'],
	},
]

export function AddressStyleStep({
	host,
	detect,
	initialStyle,
	submitting,
	onBack,
	onContinue,
}: {
	host: string
	/** `null` while the background check is still running. */
	detect: Detect | null
	/** Preselect a style — e.g. coming back from the fetch-address step. */
	initialStyle?: IWebsiteMode
	submitting: boolean
	onBack: () => void
	onContinue: (style: IWebsiteMode) => void
}) {
	const foldersBlocked = detect?.apex_cname === 'unsupported'
	const recommended: IWebsiteMode = detect?.apex_cname === 'supported' ? 'subfolder' : 'subdomain'
	const [picked, setPicked] = useState<IWebsiteMode | null>(initialStyle ?? null)
	// Derived, not synced: until the customer picks, the recommendation stands in;
	// and a folders pick can never outlive a detect that rules folders out, so
	// Continue never carries a choice that can't work.
	const wanted = picked ?? recommended
	const chosen: IWebsiteMode = wanted === 'subfolder' && foldersBlocked ? 'subdomain' : wanted

	return (
		<div className="card flex flex-col gap-5 p-5 sm:p-8">
			<div>
				<div className="label-mono mb-2">Step 3</div>
				<h2 className="font-sans text-[24px] sm:text-[28px] font-medium leading-tight text-ink">
					How should your translated pages be addressed?
				</h2>
				<p className="mt-1 text-[14px] text-ink-soft">
					Both serve the same translations — they differ in what visitors and search engines see in the
					address bar.
				</p>
			</div>

			{detect === null ? (
				<p className="rounded-nayda-sm border border-line bg-surface/50 px-4 py-3 text-[13px] text-ink-soft">
					◐ Checking what your domain can do — a few seconds…
				</p>
			) : (
				<>
					<Availability detect={detect} />

					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						{STYLES.map((style) => (
							<StyleOption
								key={style.id}
								style={style}
								host={host}
								provider={detect.dns_provider}
								selected={chosen === style.id}
								recommended={recommended === style.id}
								blocked={style.id === 'subfolder' && foldersBlocked}
								uncertain={style.id === 'subfolder' && detect.apex_cname === 'unknown'}
								onSelect={() => setPicked(style.id)}
							/>
						))}
					</div>

					{/* No switch in Settings yet (SwitchMode has no UI), so promise only what a person can do today. */}
					<p className="rounded-nayda-sm border border-line bg-surface/50 px-4 py-3 text-[13px] text-ink-soft">
						<span className="font-medium text-ink">Changed your mind later?</span> Switching isn't in the
						dashboard yet —{' '}
						<a
							href="mailto:hello@nayda.pro?subject=Switch%20address%20style"
							className="text-ink underline underline-offset-2"
						>
							write to us
						</a>{' '}
						and we'll move the site for you.
					</p>
				</>
			)}

			<div className="flex flex-wrap gap-3">
				<button
					onClick={onBack}
					disabled={submitting}
					className="btn btn-ghost whitespace-nowrap"
				>
					← Back
				</button>
				<button
					onClick={() => onContinue(chosen)}
					disabled={detect === null || submitting}
					className="btn btn-accent whitespace-nowrap"
				>
					{continueLabel(detect === null ? null : chosen, submitting)}
				</button>
			</div>
		</div>
	)
}

function continueLabel(style: IWebsiteMode | null, submitting: boolean): string {
	if (submitting) {
		return 'Setting up…'
	}
	// While the check runs nothing is chosen yet — don't name a choice.
	if (style === null) {
		return 'Continue'
	}
	return style === 'subfolder' ? 'Continue with folders →' : 'Add site with subdomains'
}

/** One sentence on what this domain can do — the question the customer actually has. */
function Availability({ detect }: { detect: Detect }) {
	if (detect.apex_cname === 'supported') {
		return (
			<p className="text-[14px] text-ink">
				Your DNS is with <span className="font-medium">{detect.dns_provider}</span>, which can do both — pick
				the one you prefer.
			</p>
		)
	}
	if (detect.apex_cname === 'unsupported') {
		return (
			<p className="text-[14px] text-ink">
				Your DNS is with <span className="font-medium">{detect.dns_provider}</span>, which can only do
				subdomains.
			</p>
		)
	}
	return (
		<p className="text-[14px] text-ink">
			We couldn't tell who runs your DNS, so both are shown. Folders only work if your provider can point a root
			domain at us.
		</p>
	)
}

function StyleOption({
	style,
	host,
	provider,
	selected,
	recommended,
	blocked,
	uncertain,
	onSelect,
}: {
	style: StyleCard
	host: string
	provider: string
	selected: boolean
	recommended: boolean
	blocked: boolean
	uncertain: boolean
	onSelect: () => void
}) {
	return (
		<button
			type="button"
			onClick={onSelect}
			disabled={blocked}
			aria-pressed={selected && !blocked}
			className={
				'card flex flex-col gap-3 p-4 text-left transition-colors ' +
				(blocked ? 'cursor-not-allowed opacity-60' : selected ? 'border-ink' : 'hover:border-line-strong')
			}
		>
			<div className="flex flex-wrap items-center gap-2">
				<span className="font-sans text-[15px] font-medium text-ink">{style.name}</span>
				{recommended && !blocked && <Chip tone="preview">Recommended</Chip>}
				{selected && !blocked && <Chip tone="live">✓ Selected</Chip>}
			</div>

			<div className="rounded-nayda-sm border border-line bg-surface/40 px-3 py-2 font-mono text-[12px] text-ink">
				{style.example(host)}
			</div>

			<ul className="flex flex-col gap-1">
				{style.good.map((g) => (
					<li
						key={g}
						className="text-[12px] text-ink"
					>
						<span className="text-[#3F7D58]">+</span> {g}
					</li>
				))}
				{style.cost.map((c) => (
					<li
						key={c}
						className="text-[12px] text-ink-soft"
					>
						<span className="text-ink-mute">−</span> {c}
					</li>
				))}
			</ul>

			{blocked && (
				<div className="mt-auto rounded-nayda-sm border border-line bg-paper px-3 py-2 text-[12px] text-ink-soft">
					<span className="font-medium text-ink">Not available on {provider}.</span> Folders need DNS that can
					point a root domain at us — Cloudflare, AWS Route 53, or any provider with ALIAS/ANAME records.
					Subdomains work on {provider} as it is.
				</div>
			)}

			{uncertain && (
				<div className="mt-auto rounded-nayda-sm border border-marigold/40 bg-paper px-3 py-2 text-[12px] text-ember">
					Only if your DNS provider can point a root domain at us — Cloudflare, AWS Route 53, or any provider
					with ALIAS/ANAME records. If you're not sure, subdomains work everywhere.
				</div>
			)}
		</button>
	)
}
